// Curated exercise video map — key = lowercased exercise name substring, value = YouTube video ID.
// We use youtube-nocookie.com (privacy-enhanced) which is more reliably embeddable
// than youtube.com and never region/consent-blocked in most geos.

const V = (id) =>
  `https://www.youtube-nocookie.com/embed/${id}?autoplay=1&mute=1&rel=0&modestbranding=1&playsinline=1`;

// Region-specific fallback videos, one per body region — guaranteed embeddable, top PT channels.
const REGION_FALLBACK = {
  neck: V("HBjR_xEYd-4"),          // Bob & Brad — chin tucks for forward head
  shoulder: V("y2ZUHmx-IFY"),      // Wall angels — foundational shoulder mobility
  thoracic: V("SxQkVD0UQNg"),      // Foam roll thoracic — classic PT drill
  core: V("MCVX9wRd_h0"),          // Athlean-X — dead bug tutorial
  lumbar: V("kqnua4rHVVA"),        // Cat-cow — spine mobility
  hip: V("Lmjidcc5ccA"),           // Dr. Jo — clamshell (hip stability)
  glute: V("XdBAhRpLrrM"),         // Bob & Brad — glute bridge (#1 back exercise)
  knee: V("f2VKMoKu8I4"),          // Master the wall sit
  ankle: V("vU_FVahd4HI"),         // Dr. Jo — runner's calf stretch
  foot: V("y1qNGxztFf8"),          // Short foot / arch lift
  general: V("MCVX9wRd_h0"),       // Dead bug — universal core default
};

export const EXERCISE_VIDEOS = {
  // ── Neck / cervical ─────────────────────────────────────────
  "chin tuck": V("HBjR_xEYd-4"),                    // Bob & Brad
  "neck retraction": V("HBjR_xEYd-4"),              // Bob & Brad
  "deep neck flexor": V("HBjR_xEYd-4"),
  "upper trap stretch": V("d0dLmYvUlLo"),
  "levator scapulae stretch": V("MEcYCUYt5sQ"),
  "scm stretch": V("y1qNGxztFf8"),
  "suboccipital release": V("dO7L4KO7yeI"),

  // ── Shoulders / thoracic ────────────────────────────────────
  "wall angel": V("y2ZUHmx-IFY"),                   // Top wall-angel tutorial
  "wall slide": V("y2ZUHmx-IFY"),
  "doorway pec stretch": V("jOdUiWMfWlw"),          // Pec stretch — form breakdown
  "pec stretch": V("jOdUiWMfWlw"),
  "pectoral stretch": V("jOdUiWMfWlw"),
  "foam roll upper back": V("SxQkVD0UQNg"),         // Classic T-spine foam roll
  "foam roll thoracic": V("SxQkVD0UQNg"),
  "thoracic extension": V("SxQkVD0UQNg"),
  "thoracic mobility": V("SxQkVD0UQNg"),
  "prone y raise": V("PZ4V0LTF2AA"),
  "prone t raise": V("z4wUUyaUn5g"),
  "prone w raise": V("m3blilqXlZM"),
  "ytw": V("PZ4V0LTF2AA"),
  "band pull apart": V("SLFDrgKarUs"),              // Athlean-X form guide
  "scapular squeeze": V("H0AvSPuyEXo"),
  "scapular retraction": V("H0AvSPuyEXo"),
  "row": V("roCP6wCXPqo"),
  "face pull": V("eIq5CB9JfKE"),                    // Athlean-X — the definitive face pull
  "external rotation": V("qYIeS5f0oCk"),

  // ── Core / lumbar ───────────────────────────────────────────
  "dead bug": V("MCVX9wRd_h0"),                     // Athlean-X — dead bug for back pain
  "bird dog": V("2_e4I-brfqs"),                     // Dr. Stuart McGill — bird dog
  "plank": V("ZyWEXjdAGCQ"),                        // Athlean-X — plank truth
  "side plank": V("XeN4pEZZJNI"),                   // Side plank step-by-step
  "glute bridge": V("XdBAhRpLrrM"),                 // Bob & Brad — #1 back exercise
  "bridge": V("XdBAhRpLrrM"),
  "cat cow": V("kqnua4rHVVA"),
  "cat-cow": V("kqnua4rHVVA"),
  "cobra": V("JDcdhTuycOI"),
  "child pose": V("2MJGg-dUKh0"),
  "child's pose": V("2MJGg-dUKh0"),
  "pelvic tilt": V("k7hbnLXFY_c"),

  // ── Hip / glute ─────────────────────────────────────────────
  "clamshell": V("Lmjidcc5ccA"),                    // Dr. Jo — clamshell
  "side lying leg lift": V("jgh6sGwtTwk"),
  "hip flexor stretch": V("-ZX1QMTdAC4"),           // Kelly Starrett — couch stretch
  "couch stretch": V("-ZX1QMTdAC4"),                // Kelly Starrett
  "figure 4 stretch": V("4UoITjubrgE"),             // Dr. Jo — 5 best sciatica stretches
  "figure four": V("4UoITjubrgE"),
  "piriformis stretch": V("4UoITjubrgE"),           // Dr. Jo
  "hamstring stretch": V("rR9euODpj_M"),            // Bob & Brad — hamstring
  "squat": V("mAvSQxCROD8"),
  "wall squat": V("f2VKMoKu8I4"),                   // Master the wall sit
  "wall sit": V("f2VKMoKu8I4"),
  "step up": V("WCFCdxzFBa4"),
  "lunge": V("QOVaHwm-Q6U"),
  "monster walk": V("q_v87JJn7f0"),
  "banded walk": V("q_v87JJn7f0"),

  // ── Foot / ankle ────────────────────────────────────────────
  "calf stretch": V("vU_FVahd4HI"),                 // Dr. Jo — runner's calf stretch
  "soleus stretch": V("vU_FVahd4HI"),
  "short foot": V("y1qNGxztFf8"),
  "short foot exercise": V("y1qNGxztFf8"),
  "towel scrunch": V("uy09TIcbGCw"),
  "toe scrunch": V("uy09TIcbGCw"),
  "single leg balance": V("uX1VushX7PM"),           // Balance stability tutorial
  "balance": V("uX1VushX7PM"),
  "heel raise": V("-M4-G8p8fmc"),
  "calf raise": V("-M4-G8p8fmc"),
  "tibialis raise": V("-M4-G8p8fmc"),
  "arch lift": V("y1qNGxztFf8"),
  "ankle mobility": V("vU_FVahd4HI"),
  "ankle circle": V("vU_FVahd4HI"),

  // ── Yoga / stretches (extra) ────────────────────────────────
  "pigeon pose": V("0_zPqA65Nok"),                  // Yoga With Adriene — pigeon
  "pigeon": V("0_zPqA65Nok"),
  "thread the needle": V("Cwakb7FNm-E"),            // 205 Academy — thread the needle
  "downward dog": V("j7rr2NmUgy4"),
  "knees to chest": V("kqnua4rHVVA"),               // Cat-cow adjacent (low back)
  "supine twist": V("kqnua4rHVVA"),
  "seated forward fold": V("rR9euODpj_M"),          // Hamstring stretch — Bob & Brad

  // ── Extra activation ───────────────────────────────────────
  "wall push up": V("ZyWEXjdAGCQ"),                 // Uses plank cues from Athlean-X
  "push up plus": V("ZyWEXjdAGCQ"),
  "serratus punch": V("ZyWEXjdAGCQ"),
  "hollow hold": V("MCVX9wRd_h0"),                  // Athlean-X dead bug — anti-extension core
  "reverse plank": V("ZyWEXjdAGCQ"),                // Athlean-X plank family
  "farmer carry": V("XdBAhRpLrrM"),                 // Glute/core stability (nearest)
  "farmer walk": V("XdBAhRpLrrM"),
  "bear crawl": V("2_e4I-brfqs"),                   // Bird dog family — quadruped core
  "marching": V("MCVX9wRd_h0"),                     // Dead bug family
  "cross crawl": V("MCVX9wRd_h0"),
  "fire hydrant": V("Lmjidcc5ccA"),                 // Clamshell family — glute med
  "donkey kick": V("XdBAhRpLrrM"),                  // Glute bridge family
  "lateral band walk": V("q_v87JJn7f0"),
  "single leg deadlift": V("XdBAhRpLrrM"),
  "romanian deadlift": V("rR9euODpj_M"),            // Hip hinge — hamstring focused
  "hip hinge": V("rR9euODpj_M"),
  "reverse lunge": V("QOVaHwm-Q6U"),
  "split squat": V("QOVaHwm-Q6U"),
  "goblet squat": V("mAvSQxCROD8"),
  "sit to stand": V("f2VKMoKu8I4"),
  "terminal knee extension": V("f2VKMoKu8I4"),
  "tke": V("f2VKMoKu8I4"),

  // ── Rolling / release ──────────────────────────────────────
  "foam roll": V("SxQkVD0UQNg"),
  "foam roll it band": V("SxQkVD0UQNg"),
  "foam roll quads": V("SxQkVD0UQNg"),
  "foam roll glutes": V("SxQkVD0UQNg"),
  "foam roll lats": V("SxQkVD0UQNg"),
  "foam roll calves": V("SxQkVD0UQNg"),
  "foam roll hamstrings": V("SxQkVD0UQNg"),
  "lacrosse ball": V("2vG4-3AVvHY"),
  "lacrosse ball chest": V("2vG4-3AVvHY"),
  "lacrosse ball glutes": V("2vG4-3AVvHY"),
  "tennis ball release": V("2vG4-3AVvHY"),
  "trigger point release": V("2vG4-3AVvHY"),
  "plantar fascia release": V("2vG4-3AVvHY"),

  // ── Neck extras ────────────────────────────────────────────
  "scapular retraction stretch": V("H0AvSPuyEXo"),
  "cross body stretch": V("d0dLmYvUlLo"),
  "sleeper stretch": V("qYIeS5f0oCk"),
  "shoulder external rotation": V("qYIeS5f0oCk"),
  "shoulder internal rotation": V("qYIeS5f0oCk"),

  // ── Yoga poses ─────────────────────────────────────────────
  "cobra pose": V("JDcdhTuycOI"),
  "sphinx pose": V("JDcdhTuycOI"),
  "cat cow pose": V("kqnua4rHVVA"),
  "child's pose stretch": V("2MJGg-dUKh0"),

  // ── Hip extras ─────────────────────────────────────────────
  "kneeling hip flexor stretch": V("-ZX1QMTdAC4"),
  "world's greatest stretch": V("-ZX1QMTdAC4"),
  "butterfly stretch": V("rR9euODpj_M"),
  "adductor stretch": V("rR9euODpj_M"),
  "frog stretch": V("rR9euODpj_M"),
  "90 90 stretch": V("Lmjidcc5ccA"),
  "supine hamstring stretch": V("rR9euODpj_M"),
  "standing hamstring stretch": V("rR9euODpj_M"),
  "quad stretch": V("-ZX1QMTdAC4"),
  "standing quad stretch": V("-ZX1QMTdAC4"),
};

// Keywords that map an exercise to a body region (used for last-resort fallback)
const REGION_KEYWORDS = {
  neck: ["neck", "cervical", "chin", "suboccipital", "scm", "levator"],
  shoulder: ["shoulder", "rotator", "cuff", "deltoid", "scapular", "scapula", "rhomboid", "trap"],
  thoracic: ["thoracic", "t-spine", "upper back", "mid back"],
  core: ["core", "abdominal", "ab ", "oblique", "transverse"],
  lumbar: ["lumbar", "lower back", "low back", "erector", "qL", "quadratus"],
  hip: ["hip", "psoas", "iliopsoas", "flexor"],
  glute: ["glute", "buttock", "piriformis"],
  knee: ["knee", "quad", "vmo", "hamstring"],
  ankle: ["ankle", "achilles", "calf", "gastroc", "soleus"],
  foot: ["foot", "arch", "plantar", "toe"],
};

// Tokenise a string into meaningful lowercase words
function tokens(s) {
  return (s || "")
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, " ")
    .split(/\s+/)
    .filter((w) => w.length > 2);
}

/**
 * Returns { url, embeddable } for an exercise.
 * - url: iframe-ready embed URL (may be a fallback region video)
 * - embeddable: true if we found an embeddable video, false → caller should show "Open on YouTube" link
 */
export function getExerciseVideo(exerciseName) {
  if (!exerciseName) return { url: REGION_FALLBACK.general, embeddable: true };

  const key = exerciseName.toLowerCase().trim();

  // 1. Exact key match
  if (EXERCISE_VIDEOS[key]) return { url: EXERCISE_VIDEOS[key], embeddable: true };

  // 2. Substring match (both directions)
  for (const k of Object.keys(EXERCISE_VIDEOS)) {
    if (key.includes(k) || k.includes(key)) return { url: EXERCISE_VIDEOS[k], embeddable: true };
  }

  // 3. Token-overlap match — pick the key with the most word overlap
  const qTokens = tokens(key);
  let best = null;
  let bestScore = 0;
  for (const k of Object.keys(EXERCISE_VIDEOS)) {
    const kTokens = tokens(k);
    const overlap = kTokens.filter((t) => qTokens.includes(t)).length;
    if (overlap > bestScore) {
      bestScore = overlap;
      best = k;
    }
  }
  if (best && bestScore >= 1) return { url: EXERCISE_VIDEOS[best], embeddable: true };

  // 4. Region fallback — pick a body-region default video
  for (const [region, kws] of Object.entries(REGION_KEYWORDS)) {
    if (kws.some((kw) => key.includes(kw))) {
      return { url: REGION_FALLBACK[region], embeddable: true };
    }
  }

  // 5. Absolute last resort — universal core video (always embeddable, never blank)
  return { url: REGION_FALLBACK.general, embeddable: true };
}

// Regular YouTube search URL (opens in new tab) — used as extra "Search more on YouTube" link
export function getYouTubeSearchUrl(exerciseName) {
  const q = encodeURIComponent(`${exerciseName} exercise demo physical therapy`);
  return `https://www.youtube.com/results?search_query=${q}`;
}

// Extract the YouTube video ID from any embed URL we generate
function extractVideoId(embedUrl) {
  if (!embedUrl) return null;
  const m = embedUrl.match(/\/embed\/([A-Za-z0-9_-]{6,15})/);
  return m ? m[1] : null;
}

/**
 * Returns a YouTube thumbnail URL (mqdefault ~320x180) for an exercise.
 * Uses the same matching cascade as getExerciseVideo so previews always match the demo.
 */
export function getExerciseThumbnail(exerciseName) {
  const { url } = getExerciseVideo(exerciseName);
  const id = extractVideoId(url);
  return id ? `https://i.ytimg.com/vi/${id}/mqdefault.jpg` : null;
}
